import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  Dialog,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Client } from "../../@data";
import { verifyClientPassword } from "../../services/clients";
import Button from "../button";
import ModalCloseButton from "../modal-close-button";
import {
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
} from "../../styles/modal-styles";

export interface PasswordModalRef {
  open: (client: Client, onSuccess?: () => void) => void;
  close: () => void;
}

interface PasswordModalProps {
  loading?: boolean;
}

const PasswordModal = forwardRef<PasswordModalRef, PasswordModalProps>(
  ({ loading = false }, ref) => {
    const [open, setOpen] = useState(false);
    const [client, setClient] = useState<Client | null>(null);
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [onSuccessCallback, setOnSuccessCallback] = useState<
      (() => void) | null
    >(null);

    useImperativeHandle(ref, () => ({
      open: (clientData: Client, onSuccess?: () => void) => {
        setClient(clientData);
        setPassword("");
        setShowPassword(false);
        setErrors([]);
        setOnSuccessCallback(() => onSuccess || null);
        setOpen(true);
      },
      close: () => {
        setOpen(false);
        setClient(null);
        setPassword("");
        setShowPassword(false);
        setErrors([]);
        setOnSuccessCallback(null);
      },
    }));

    const handleClose = () => {
      if (!loading) {
        setOpen(false);
        setClient(null);
        setPassword("");
        setShowPassword(false);
        setErrors([]);
        setOnSuccessCallback(null);
      }
    };

    const handleConfirm = async () => {
      const newErrors: string[] = [];

      if (!password.trim()) {
        newErrors.push("Senha é obrigatória");
      }

      if (newErrors.length > 0) {
        setErrors(newErrors);
        return;
      }

      // Verifica a senha
      if (client && verifyClientPassword(client, password)) {
        // Senha correta - executa o callback e fecha a modal
        if (onSuccessCallback) {
          onSuccessCallback();
        }
        handleClose();
      } else {
        // Senha incorreta
        setErrors(["Senha incorreta. Tente novamente."]);
      }
    };

    const handleForgotPassword = () => {
      if (client) {
        const message = `Olá, gostaria de recuperar a senha do usuário ${client.name}`;
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/5551998368882?text=${encodedMessage}`;
        window.open(whatsappUrl, "_blank");
      }
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1.5rem" } }}
        disableEscapeKeyDown={loading}
      >
        <StyledDialogTitle sx={{ pb: 1 }}>
          Verificação de Senha
          <ModalCloseButton onClose={handleClose} disabled={loading} />
        </StyledDialogTitle>

        <StyledDialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Digite sua senha para continuar com o agendamento
            </Typography>

            {errors.length > 0 && (
              <Alert severity="error">
                {errors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Senha"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ mt: 1, textAlign: "center" }}>
              <Button
                variant="text"
                color="primary"
                onClick={handleForgotPassword}
                size="small"
              >
                Esqueci minha senha
              </Button>
            </Box>
          </Box>
        </StyledDialogContent>

        <StyledDialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={loading || !password.trim()}
            loading={loading}
            loadingText="Verificando..."
          >
            Confirmar
          </Button>
        </StyledDialogActions>
      </Dialog>
    );
  }
);

PasswordModal.displayName = "PasswordModal";

export default PasswordModal;
